import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, BookOpen, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/risk-education")({
  head: () => ({
    meta: [
      { title: "Understanding Forex Risk — ForexMind AI" },
      {
        name: "description",
        content:
          "Educational resources to help you understand forex and CFD trading risk before using ForexMind AI market intelligence.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "ForexMind AI" },
      { property: "og:title", content: "Understanding Forex Risk — ForexMind AI" },
      {
        property: "og:description",
        content:
          "Educational resources to help you understand forex and CFD trading risk before using ForexMind AI market intelligence.",
      },
      { property: "og:url", content: "https://forexmind.ai/risk-education" },
      { property: "og:image", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:secure_url", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Understanding Forex Risk — ForexMind AI" },
      {
        name: "twitter:description",
        content:
          "Educational resources to help you understand forex and CFD trading risk before using ForexMind AI market intelligence.",
      },
      { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
    ],
    links: [{ rel: "canonical", href: "https://forexmind.ai/risk-education" }],
  }),
  component: RiskEducationPage,
});

function RiskEducationPage() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-12 text-foreground">
      <Button asChild variant="ghost" size="sm" className="text-muted-foreground">
        <Link to="/">
          <ArrowLeft className="mr-1.5 h-3 w-3" /> Back to home
        </Link>
      </Button>

      <div className="mt-6 rounded-lg border border-warn/40 bg-warn/10 p-4 text-warn">
        <div className="mb-1 flex items-center gap-2 font-semibold">
          <AlertTriangle className="h-4 w-4" /> Take time to learn the risks first
        </div>
        <p className="text-[12.5px] leading-snug">
          ForexMind AI is an educational platform for market intelligence — not a place to
          learn trading from scratch. We strongly recommend completing a free foundational
          course before unlocking market intelligence.
        </p>
      </div>

      <h1 className="mt-8 font-display text-3xl font-bold">Before you continue</h1>

      <div className="mt-6 space-y-5 text-sm leading-relaxed text-muted-foreground">
        <p>
          Forex and CFD trading involves <strong>significant risk of loss</strong>, including
          loss of more than your initial deposit at some brokers due to leverage. Most retail
          traders lose money.
        </p>
        <p>
          We recommend the following free resources before continuing:
        </p>
        <ul className="space-y-2 pl-5 [list-style:disc]">
          <li>
            <a
              className="underline hover:text-foreground"
              href="https://www.babypips.com/learn/forex"
              target="_blank"
              rel="noreferrer noopener"
            >
              BabyPips — School of Pipsology
            </a>{" "}
            (full beginner curriculum, free)
          </li>
          <li>
            <a
              className="underline hover:text-foreground"
              href="https://www.investopedia.com/forex-4427766"
              target="_blank"
              rel="noreferrer noopener"
            >
              Investopedia — Forex basics
            </a>
          </li>
          <li>
            <Link className="underline hover:text-foreground" to="/risk-disclosure">
              Our full risk disclosure
            </Link>
          </li>
        </ul>
        <p>
          When you feel you understand the risks involved, you can return to{" "}
          <Link to="/onboarding" className="underline hover:text-foreground">
            onboarding
          </Link>{" "}
          and continue.
        </p>
      </div>

      <nav className="mt-10 flex flex-wrap gap-4 border-t border-border/60 pt-6 text-xs text-muted-foreground">
        <Link to="/risk-disclosure" className="hover:text-foreground">Risk Disclosure</Link>
        <Link to="/performance-methodology" className="hover:text-foreground inline-flex items-center gap-1">
          <BookOpen className="h-3 w-3" /> Performance Methodology
        </Link>
        <a href="mailto:support@forexmind.ai" className="hover:text-foreground">Contact</a>
      </nav>
    </main>
  );
}
